export const initialPage = {
	page: null,
	pageStartIndex: null,
	pageStopIndex: null,
	pageSize: null,
	totalItems: null,
	lastEvaluatedKey: null,
};

const updatePage = (page, params) => {
	const uniqueData = [];
	const uniqueIds = [];
	for (const item of page) {
		if(item) {
			const identifier = (item.identifier.sk) ? JSON.stringify(item.identifier.sk) : JSON.stringify(item.identifier.gsisk);
			if(!uniqueIds.includes(identifier)) {
				uniqueData.push(item);
				uniqueIds.push(identifier);
			}
		}
	}
	if(params.isInverted) uniqueData.reverse();
	return uniqueData;
};

const sortData = (data, isInverted) => {
	const sortedItems = data.sort((a, b) => {
		const identifierA = (a.identifier.sk) ? JSON.stringify(a.identifier.sk) : JSON.stringify(a.identifier.gsisk);
		const identifierB = (b.identifier.sk) ? JSON.stringify(b.identifier.sk) : JSON.stringify(b.identifier.gsisk);
		return (identifierA < identifierB) ? -1 : (identifierA > identifierB) ? 1 : 0;
	});
	if (isInverted) sortedItems.reverse();
	return sortedItems;
};

const addUniqueData = (state, action, params) => {
	const uniqueData = [];
	const uniqueIds = [];

	// Add unique ids from state
	for (const item of state[params.pageName]) {
		if(!item || !item.identifier) continue;
		const identifier = (item.identifier.sk) ? JSON.stringify(item.identifier.sk) : JSON.stringify(item.identifier.gsisk);
		if(!uniqueIds.includes(identifier)) {
			uniqueData.push(item);
			uniqueIds.push(identifier);
		}
	}

	for (const item of action[params.pageName]) {
		if(!item || !item.identifier) continue;
		const identifier = (item.identifier.sk) ? JSON.stringify(item.identifier.sk) : JSON.stringify(item.identifier.gsisk);
		if(!uniqueIds.includes(identifier)) {
			uniqueData.push(item);
			uniqueIds.push(identifier);
		}
	}

	return sortData(uniqueData, params.isInverted);
};

export const reducerDefaults = {
	initialPage,
	initialState: (params) => ({
		[params.pageName]: [],
		[params.itemName]: null,
		...initialPage,
	}),
	getPage: (state, action, params) => {
		// Ensure only unique items are being added
		const uniqueData = addUniqueData(state, action, params);
		// Manage forward/backward keys
		const isInit = !!((!state.lastEvaluatedKey && !action.lastEvaluatedKey) || (!state.lastEvaluatedKey && JSON.stringify(state.lastEvaluatedKey) !== JSON.stringify(action.lastEvaluatedKey)));
		const isLast = !(action.lastEvaluatedKey);
		const isNext = !!(state.lastEvaluatedKey && JSON.stringify(state.lastEvaluatedKey) !== JSON.stringify(action.lastEvaluatedKey));
		const isPrev = !!(JSON.stringify(state.lastEvaluatedKey) === JSON.stringify(action.lastEvaluatedKey));
		// Init holding variables
		const pageSize = params.isSingleItem ? 1 : action.pageSize;

		let pageStartIndex;
		let pageStopIndex;
		let lastEvaluatedKey;
		if (isInit) {
			pageStartIndex = 0;
			pageStopIndex = parseInt(pageSize);
			lastEvaluatedKey = action.lastEvaluatedKey;
		}
		else if (isLast) {
			pageStartIndex = parseInt(state.pageStartIndex) + parseInt(pageSize);
			pageStopIndex = parseInt(action.totalItems);
			lastEvaluatedKey = null;
		}
		else if (isNext) {
			pageStartIndex = parseInt(state.pageStartIndex) + parseInt(pageSize);
			pageStopIndex = parseInt(state.pageStartIndex) + 2 * parseInt(pageSize);
			lastEvaluatedKey = action.lastEvaluatedKey;
		}
		else if (isPrev) {
			pageStartIndex = parseInt(state.pageStartIndex) + parseInt(pageSize);
			pageStopIndex = parseInt(state.pageStartIndex) + 2 * parseInt(pageSize);
		}

		return {
			...state,
			[params.pageName]: uniqueData,
			pageStartIndex,
			pageStopIndex,
			page: (action.page) ? action.page : state.page,
			pageSize: params.isSingleItem ? 1 : (action.pageSize) ? action.pageSize : state.pageSize,
			totalItems: (action.totalItems) ? action.totalItems : state.totalItems,
			lastEvaluatedKey,
		};
	},
	clearPages: (state, params) => ({
		...state,
		[params.pageName]: [],
		page: null,
		pageStartIndex: null,
		pageStopIndex: null,
		pageSize: null,
		totalItems: null,
		lastEvaluatedKey: null,
	}),
	clearItem: (state, params) => ({
		...state,
		[params.itemName]: null,
	}),
	getExistingPage: (state, action) => ({
		...state,
		page: action.page,
		pageStartIndex: parseInt(state.pageSize) * (parseInt(action.page) - 1),
		pageStopIndex: parseInt(state.pageSize) * parseInt(action.page),
	}),
	getItem: (state, action, params) => ({
		...state,
		[params.itemName]: (action[params.itemName]) ? action[params.itemName] : null
	}),
	setActive: (state, action, params) => ({
		...state,
		[params.itemName]: (params.itemId) ? state[params.pageName].find((item) => item[params.itemId] === action[params.itemId]) : null
	}),
	unsetActive: (state, params) => ({
		...state,
		[params.itemName]: null,
	}),
	postItem: (state, action, params) => {

		const updatedData = updatePage((params.itemName !== 'profilePicture') ? [ ...state[params.pageName], action[params.itemName] ]
			: [ action[params.itemName], ...state[params.pageName] ], params);

		const sortedUpdatedData = sortData(updatedData, params.isInverted);

		return {
			...state,
			[params.pageName]: sortedUpdatedData,
			[params.itemName]: action[params.itemName],
			pageStartIndex: state.pageStartIndex ? state.pageStartIndex : 0,
			pageStopIndex: state.pageStopIndex ? state.pageStopIndex : params.isSingleItem ? 1 : 50,
			page: state.page ? state.page : 1,
			pageSize: state.pageSize ? state.pageSize : params.isSingleItem ? 1 : 50,
			totalItems: state.totalItems ? state.totalItems += 1 : 1,
		};
	},
	putItem: (state, action, params) => {
		const index = state[params.pageName].findIndex(item => item.identifier.sk === action[params.itemName].identifier.sk);
		if(index !== -1) {
			const updatedPage = [ ...state[params.pageName] ];
			updatedPage[index] = action[params.itemName];
			return {
				...state,
				[params.pageName]: updatedPage,
				[params.itemName]: action[params.itemName],
			};
		}
		else {
			return {
				...state,
				[params.itemName]: action[params.itemName],
			};
		}
	},
	deleteItem: (state, action, params) => {
		const pageIndex = state[params.pageName].findIndex(item => item.identifier.sk === action.identifier.sk);
		if(pageIndex !== -1) {
			const updatedPage = [ ...state[params.pageName] ];
			updatedPage.splice(pageIndex, 1);

			const isActiveItem = state[params.itemName] && state[params.itemName].identifier.sk === action.identifier.sk;
			const newTotalItems = state.totalItems - 1;

			// Calculate new pageStartIndex and pageStopIndex
			let newPageStartIndex = state.pageStartIndex;
			let newPageStopIndex = state.pageStopIndex;
			let newPage = state.page;

			// If the deleted item was the only item on the current page
			if (newTotalItems === 0) {
				newPageStartIndex = null;
				newPageStopIndex = null;
			}
			else if (state.pageSize === 1 && newPageStartIndex === pageIndex) {
				// The deleted item was the first item on the current page
				newPageStartIndex = pageIndex - 1;
				newPageStopIndex = pageIndex;
				newPage = state.page - 1;
			}

			const activeItem = (isActiveItem && newTotalItems > 0) ? updatedPage[pageIndex] :
				(isActiveItem && newTotalItems === 0)? null
					: state[params.itemName];

			const res = {
				...state,
				[params.pageName]: updatedPage,
				[params.itemName]: activeItem,
				totalItems: newTotalItems,
				pageStartIndex: newPageStartIndex,
				pageStopIndex: newPageStopIndex,
				page: newPage,
			};
			return res;
		}
		else return state;
	}
};